/**
 * AllocationBarChart - Horizontal bar chart for allocation visualization
 * Alternative view to pie chart, better for comparing multiple items
 */

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

export interface AllocationData {
  name: string;
  value: number;
  color?: string;
}

export interface AllocationBarChartProps {
  data: AllocationData[];
  title?: string;
  showLegend?: boolean;
  showTooltip?: boolean;
  height?: number;
  className?: string;
  colors?: string[];
}

const DEFAULT_COLORS = [
  '#002B45', // Navy
  '#F7B500', // Gold
  '#28A745', // Green
  '#DC3545', // Red
  '#6C757D', // Gray
  '#17A2B8', // Cyan
  '#FFC107', // Amber
  '#6610F2', // Purple
];

const AllocationBarChart: React.FC<AllocationBarChartProps> = ({
  data,
  title,
  showLegend = true,
  showTooltip = true,
  height = 300,
  className = '',
  colors = DEFAULT_COLORS
}) => {
  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-semibold text-gray-900">{payload[0].name}</p>
          <p className="text-sm text-gray-600">
            {payload[0].value.toFixed(2)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Format data for bar chart
  const chartData = data.map(item => ({
    name: item.name,
    value: item.value,
    color: item.color || colors[0]
  }));

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            type="number"
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis
            type="category"
            dataKey="name"
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
            width={70}
          />
          {showTooltip && <Tooltip content={<CustomTooltip />} />}
          {showLegend && <Legend />}
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AllocationBarChart;
